/**
 * 브라우저 감지 및 외부 브라우저 열기 유틸리티
 */

/**
 * 카카오톡 인앱 브라우저인지 확인
 */
export const isKakaoTalkInAppBrowser = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes('kakaotalk');
};

/**
 * 네이버 인앱 브라우저인지 확인
 */
export const isNaverInAppBrowser = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes('naver');
};

/**
 * 인스타그램 인앱 브라우저인지 확인
 */
export const isInstagramInAppBrowser = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes('instagram');
};

/**
 * 페이스북 인앱 브라우저인지 확인
 */
export const isFacebookInAppBrowser = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes('fban') || userAgent.includes('fbav');
};

/**
 * 인앱 브라우저인지 확인 (주요 앱들)
 */
export const isInAppBrowser = (): boolean => {
  return (
    isKakaoTalkInAppBrowser() ||
    isNaverInAppBrowser() ||
    isInstagramInAppBrowser() ||
    isFacebookInAppBrowser()
  );
};

/**
 * 어떤 인앱 브라우저인지 반환
 */
export const getInAppBrowserType = (): string | null => {
  if (isKakaoTalkInAppBrowser()) return 'kakaotalk';
  if (isNaverInAppBrowser()) return 'naver';
  if (isInstagramInAppBrowser()) return 'instagram';
  if (isFacebookInAppBrowser()) return 'facebook';
  return null;
};

/**
 * iOS인지 확인
 */
export const isIOS = (): boolean => {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
};

/**
 * Android인지 확인
 */
export const isAndroid = (): boolean => {
  return /android/i.test(navigator.userAgent);
};

/**
 * 외부 브라우저에서 현재 URL 열기
 * @param url 열 URL (기본값: 현재 URL)
 * @returns iOS에서 클립보드 복사 후 Safari 열기가 필요한 경우 true 반환
 */
export const openInExternalBrowser = (url?: string): boolean => {
  const targetUrl = url || window.location.href;

  if (isKakaoTalkInAppBrowser()) {
    // 카카오톡: iOS/Android 모두 kakaotalk://web/openExternal 스킴 사용
    window.location.href = 'kakaotalk://web/openExternal?url=' + encodeURIComponent(targetUrl);
    return false;
  } else if (isAndroid()) {
    // Android 기타 인앱 브라우저: intent URL scheme 사용
    const intentUrl = `intent://${targetUrl.replace(/^https?:\/\//, '')}#Intent;scheme=https;package=com.android.chrome;end`;
    window.location.href = intentUrl;
    return false;
  } else if (isIOS()) {
    // iOS 기타 인앱 브라우저: 클립보드 복사 후 Safari 열기
    copyToClipboard(targetUrl);
    setTimeout(() => {
      window.location.href = 'x-web-search://?';
    }, 100);
    return true;
  } else {
    // 기타: 새 창으로 열기 시도
    window.open(targetUrl, '_blank');
    return false;
  }
};

/**
 * 클립보드에 텍스트 복사
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const result = document.execCommand('copy');
    document.body.removeChild(textArea);
    return result;
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
};

/**
 * 카카오톡 인앱 브라우저 감지 시 외부 브라우저 열기 안내 정보
 */
export interface InAppBrowserInfo {
  isInApp: boolean;
  browserType: string | null;
  message: string;
  instruction: string;
}

export const getInAppBrowserInfo = (): InAppBrowserInfo => {
  const browserType = getInAppBrowserType();

  if (!browserType) {
    return {
      isInApp: false,
      browserType: null,
      message: '',
      instruction: '',
    };
  }

  const messages: Record<string, { message: string; instruction: string }> = {
    kakaotalk: {
      message: '카카오톡 브라우저에서는 일부 기능이 제한될 수 있습니다.',
      instruction: isAndroid()
        ? '아래 버튼을 눌러 외부 브라우저로 열어주세요.'
        : '우측 상단 ⋮ 메뉴에서 "다른 브라우저로 열기"를 선택해주세요.',
    },
    naver: {
      message: '네이버 앱 브라우저에서는 일부 기능이 제한될 수 있습니다.',
      instruction: '우측 하단 메뉴에서 "기본 브라우저로 열기"를 선택해주세요.',
    },
    instagram: {
      message: '인스타그램 브라우저에서는 일부 기능이 제한될 수 있습니다.',
      instruction: '우측 상단 ⋮ 메뉴에서 "브라우저에서 열기"를 선택해주세요.',
    },
    facebook: {
      message: '페이스북 브라우저에서는 일부 기능이 제한될 수 있습니다.',
      instruction: '우측 하단 ⋯ 메뉴에서 "브라우저로 열기"를 선택해주세요.',
    },
  };

  return {
    isInApp: true,
    browserType,
    ...messages[browserType],
  };
};
